import { useEffect, useState } from "react";
import axios from "axios";
import { Palette, Upload, Save, RotateCcw, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { useBranding } from "../context/BrandingContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 2 * 1024 * 1024;

const ASSETS = [
  { key: "logo_main", label: "Logo principal", accept: ".png,.svg", types: ["image/png", "image/svg+xml"] },
  { key: "logo_light", label: "Logo modo claro", accept: ".png,.svg", types: ["image/png", "image/svg+xml"] },
  { key: "logo_dark", label: "Logo modo oscuro", accept: ".png,.svg", types: ["image/png", "image/svg+xml"] },
  { key: "favicon", label: "Favicon", accept: ".png,.svg,.ico", types: ["image/png", "image/svg+xml", "image/x-icon", "image/vnd.microsoft.icon"] },
];

const ColorField = ({ label, value, onChange, testid }) => (
  <label className="text-sm">
    <span className="block text-xs font-bold text-gray-500 uppercase mb-1">{label}</span>
    <div className="flex items-center gap-2">
      <input data-testid={testid} type="color" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-12 h-10 rounded-lg border border-gray-200 cursor-pointer p-1 bg-white" />
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono" maxLength={7} />
    </div>
  </label>
);

export default function BrandingSettings() {
  const { refreshBranding } = useBranding();
  const [form, setForm] = useState(null);
  const [current, setCurrent] = useState(null);
  const [staged, setStaged] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    axios.get(`${API}/branding`).then((r) => {
      setCurrent(r.data);
      setForm({ app_name: r.data.app_name, tagline: r.data.tagline, colors: { ...r.data.colors } });
      setStaged({});
      setErrors({});
    }).catch(() => {});
  };

  useEffect(load, []);

  const stageFile = (assetKey, file, allowedTypes) => {
    if (!file) return;
    if (!allowedTypes.includes(file.type)) {
      setErrors((e) => ({ ...e, [assetKey]: "Formato no permitido" }));
      return;
    }
    if (file.size > MAX_BYTES) {
      setErrors((e) => ({ ...e, [assetKey]: "Máximo 2 MB" }));
      return;
    }
    setErrors((e) => ({ ...e, [assetKey]: null }));
    setStaged((s) => ({ ...s, [assetKey]: { file, url: URL.createObjectURL(file) } }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.put(`${API}/admin/branding`, form);
      for (const [key, item] of Object.entries(staged)) {
        const fd = new FormData();
        fd.append("file", item.file);
        await axios.post(`${API}/admin/branding/upload/${key}`, fd);
      }
      refreshBranding();
      load();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo guardar el branding.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setResetting(true);
    setError(null);
    try {
      await axios.post(`${API}/admin/branding/reset`);
      refreshBranding();
      load();
    } catch {
      setError("No se pudo restaurar el branding.");
    } finally {
      setResetting(false);
    }
  };

  if (!form) return null;

  const previewLogo = staged.logo_main?.url ||
    (current?.assets?.logo_main ? `${BACKEND}${current.assets.logo_main}` : null);
  const previewFavicon = staged.favicon?.url ||
    (current?.assets?.favicon ? `${BACKEND}${current.assets.favicon}` : null);

  return (
    <div data-testid="admin-branding" className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6">
      <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
        <Palette className="w-5 h-5 text-purple-600" /> Branding — identidad visual
      </h3>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Formulario */}
        <div className="space-y-4">
          <label className="text-sm block">
            <span className="block text-xs font-bold text-gray-500 uppercase mb-1">Nombre de la aplicación</span>
            <input data-testid="branding-name" type="text" value={form.app_name} maxLength={40}
              onChange={(e) => setForm({ ...form, app_name: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm" />
          </label>
          <label className="text-sm block">
            <span className="block text-xs font-bold text-gray-500 uppercase mb-1">Eslogan</span>
            <input data-testid="branding-tagline" type="text" value={form.tagline} maxLength={120}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <ColorField label="Primario" value={form.colors.primary} testid="branding-color-primary"
              onChange={(v) => setForm({ ...form, colors: { ...form.colors, primary: v } })} />
            <ColorField label="Secundario" value={form.colors.secondary} testid="branding-color-secondary"
              onChange={(v) => setForm({ ...form, colors: { ...form.colors, secondary: v } })} />
            <ColorField label="Acento" value={form.colors.accent} testid="branding-color-accent"
              onChange={(v) => setForm({ ...form, colors: { ...form.colors, accent: v } })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {ASSETS.map((a) => (
              <div key={a.key} className="border border-gray-100 rounded-xl p-3">
                <span className="block text-xs font-bold text-gray-500 uppercase mb-2">{a.label}</span>
                <div className="flex items-center gap-3">
                  {(staged[a.key]?.url || current?.assets?.[a.key]) ? (
                    <img
                      src={staged[a.key]?.url || `${BACKEND}${current.assets[a.key]}`}
                      alt={a.label}
                      className="h-10 w-10 object-contain rounded-lg bg-gray-50 border border-gray-100"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-lg bg-gray-50 border border-dashed border-gray-200" />
                  )}
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg px-3 py-2 cursor-pointer transition-all">
                    <Upload className="w-3.5 h-3.5" /> Subir
                    <input data-testid={`branding-upload-${a.key}`} type="file" accept={a.accept} className="hidden"
                      onChange={(e) => stageFile(a.key, e.target.files[0], a.types)} />
                  </label>
                </div>
                {errors[a.key] && (
                  <p className="flex items-center gap-1 text-xs text-red-600 mt-2"><AlertTriangle className="w-3 h-3" /> {errors[a.key]}</p>
                )}
                {staged[a.key] && !errors[a.key] && (
                  <p className="text-[11px] text-green-600 mt-2">Listo para guardar: {staged[a.key].file.name}</p>
                )}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-400">PNG o SVG (favicon también ICO) · máximo 2 MB por archivo.</p>
        </div>

        {/* Vista previa */}
        <div>
          <span className="block text-xs font-bold text-gray-500 uppercase mb-2">Vista previa</span>
          <div data-testid="branding-preview" className="rounded-2xl border border-gray-100 overflow-hidden">
            <div className="p-6" style={{ background: `linear-gradient(120deg, ${form.colors.primary}, ${form.colors.secondary})` }}>
              <div className="flex items-center gap-3">
                {previewLogo ? (
                  <img src={previewLogo} alt="logo" className="h-10 w-auto object-contain" />
                ) : (
                  <span className="text-2xl font-extrabold text-white">{form.app_name || "Nombre"}</span>
                )}
              </div>
              <p className="text-white/90 text-sm mt-2">{form.tagline || "Eslogan de la aplicación"}</p>
            </div>
            <div className="p-6 bg-gray-50 flex flex-wrap items-center gap-3">
              <button className="px-5 py-2.5 rounded-xl text-sm font-bold text-white shadow"
                style={{ background: `linear-gradient(90deg, ${form.colors.primary}, ${form.colors.secondary})` }}>
                Botón principal
              </button>
              <span className="text-sm font-semibold" style={{ color: form.colors.accent }}>Enlace de acento →</span>
              {previewFavicon && (
                <span className="flex items-center gap-1.5 text-xs text-gray-500 ml-auto">
                  <img src={previewFavicon} alt="favicon" className="w-5 h-5 object-contain" /> favicon
                </span>
              )}
            </div>
          </div>

          {error && <p data-testid="branding-error" className="text-sm text-red-600 font-semibold mt-3">{error}</p>}

          <div className="flex flex-wrap gap-3 mt-4">
            <button data-testid="branding-save" onClick={save} disabled={saving || Object.values(errors).some(Boolean)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-60 transition-all">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <CheckCircle2 className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              {saving ? "Guardando..." : saved ? "¡Guardado!" : "Guardar branding"}
            </button>
            <button data-testid="branding-reset" onClick={reset} disabled={resetting}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 disabled:opacity-60 transition-all">
              <RotateCcw className="w-4 h-4" /> {resetting ? "Restaurando..." : "Restaurar por defecto"}
            </button>
          </div>
          <p className="text-[11px] text-gray-400 mt-2">Los cambios se aplican al instante en toda la aplicación, sin redesplegar.</p>
        </div>
      </div>
    </div>
  );
}
